import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowRight, Target, Hash, MessageSquare, Sparkles } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { CodyFullLogo } from '../components/CodyLogo';
import redditPixel from '../lib/tracking';
import { useAuth } from '../lib/auth';

export default function ScanProgress() {
  const [step, setStep] = useState('analyzing');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const url = sessionStorage.getItem('scanUrl');
    if (!url) { navigate('/'); return; }
    const run = async () => {
      try {
        const resp = await fetch('/api/scan', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }) });
        if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.detail || 'Analysis failed'); }
        const data = await resp.json();
        setResult(data);
        sessionStorage.setItem('scanResults', JSON.stringify(data));
        redditPixel.scanCompleted(data.total_leads || 0);
        setStep('results');
      } catch (err) { setError(err.message || 'Analysis failed.'); }
    };
    run();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4">
      {/* Subtle background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div className="absolute w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,69,0,0.08) 0%, transparent 70%)', top: '-10%', left: '10%' }} animate={{ x: [0, 20, 0] }} transition={{ duration: 15, repeat: Infinity }} />
        <motion.div className="absolute w-[400px] h-[400px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.05) 0%, transparent 70%)', bottom: '0%', right: '15%' }} animate={{ x: [0, -15, 0] }} transition={{ duration: 12, repeat: Infinity }} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <CodyFullLogo height={36} className="brightness-0 invert" />
        </div>

        {/* Analyzing State */}
        {step === 'analyzing' && !error && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-8 rounded-2xl border-0 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.2)]">
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-[#FF4500] animate-spin mx-auto mb-4" />
                <h2 className="text-lg font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Analyzing your website</h2>
                <p className="text-sm text-[#5B667A]">Extracting keywords and finding subreddits...</p>
                <Progress value={45} className="h-1.5 mt-5" />
              </div>
            </Card>
          </motion.div>
        )}

        {/* Results State */}
        {step === 'results' && result && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
            <Card className="p-8 rounded-2xl border-0 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.2)]">
              <h2 className="text-xl font-bold text-center mb-1" style={{ fontFamily: "'Space Grotesk'" }}>
                We found opportunities for <span className="text-[#FF4500]">{result.product_name}</span>
              </h2>
              <p className="text-xs text-[#5B667A] text-center mb-6 line-clamp-2">{result.product_description}</p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { icon: Hash, value: result.keywords?.length || 0, label: 'Keywords', color: '#FF4500' },
                  { icon: MessageSquare, value: result.suggested_subreddits?.length || 0, label: 'Subreddits', color: '#2563EB' },
                  { icon: Target, value: '50+', label: 'Est. Leads', color: '#16A34A' },
                ].map((s, i) => (
                  <div key={i} className="relative p-4 rounded-xl bg-[#FAFBFC] border border-[#E6E8EE]/60 text-center overflow-hidden">
                    <div className="absolute top-2 right-2 opacity-[0.07]"><s.icon className="w-8 h-8" style={{ color: s.color }} /></div>
                    <p className="text-2xl font-extrabold relative" style={{ fontFamily: "'Space Grotesk'", color: s.color }}>{s.value}</p>
                    <p className="text-[10px] text-[#5B667A] font-medium relative">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <Button className="w-full h-12 rounded-xl text-sm font-bold cta-btn shadow-lg shadow-[#FF4500]/20"
                style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
                onClick={() => navigate(user ? '/onboard' : '/signup', { replace: true })}>
                {user ? 'Set Up Your Dashboard' : 'Create Free Account to Continue'} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              {!user && <p className="text-[10px] text-[#9ca3af] text-center mt-3">Free plan · 3 leads/day · 5 keywords · 5 subreddits</p>}
            </Card>
          </motion.div>
        )}

        {/* Error */}
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="p-8 rounded-2xl border-0 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.2)]">
              <div className="text-center">
                <p className="text-sm text-red-500 mb-4">{error}</p>
                <Button variant="outline" onClick={() => navigate('/')}>Try Again</Button>
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
